import React, { useState, useEffect } from 'react';
import { Check, Crown, Star, Gem, Shield, Award, CreditCard, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import PaymentGateway from './PaymentGateway';
import { toast } from 'sonner';
import { getErrorMessage } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { companyRegistrationApi, paymentsApi, profileApi } from '../services/api';

const tierToProductId = {
  founder: 'membership_founder',
  titanium: 'membership_titanium',
  rhodium: 'membership_rhodium',
  platinum: 'membership_platinum',
  gold: 'membership_gold',
  silver: 'membership_silver'
};

const normalizeTierForCompare = (t) => (t || '').toLowerCase().replace(/\s+/g, '_');
const tierOrderRank = {
  silver: 1,
  gold: 2,
  platinum: 3,
  rhodium: 4,
  titanium: 5,
  founder: 6
};
const getTierRank = (tierValue) => tierOrderRank[normalizeTierForCompare(tierValue)] || 0;

const formatTierLimit = (value, noun) => {
  if (value === 0) return `Unlimited ${noun}`;
  if (value == null || value === '') return `- ${noun}`;
  return `${Number(value).toLocaleString()} ${noun}`;
};

const calculateMonthlyFromAnnual = (annualValue) => {
  const annual = Number(annualValue || 0);
  if (!annual || annual <= 0) return 0;
  return Math.ceil(annual / 12);
};

const formatUsd = (value) => `USD ${Number(value || 0).toLocaleString()}`;

// Match AdminTiers icons for consistency
const getTierIcon = (tierId) => {
  const icons = {
    silver: <Star size={40} className="mx-auto mb-3 text-gray-500" />,
    gold: <Crown size={40} className="mx-auto mb-3 text-amber-500" />,
    platinum: <Gem size={40} className="mx-auto mb-3 text-blue-500" />,
    founder: <Shield size={40} className="mx-auto mb-3 text-purple-500" />
  };
  return icons[tierId?.toLowerCase()] || <Award size={40} className="mx-auto mb-3 text-gray-500" />;
};

const Upgrade = () => {
  const { member } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedTier, setSelectedTier] = useState(null);
  const [paymentRecord, setPaymentRecord] = useState(null);
  const [creatingRecord, setCreatingRecord] = useState(false);
  const [companyRegistration, setCompanyRegistration] = useState(null);
  const [showRegistrationDialog, setShowRegistrationDialog] = useState(false);
  const upgradingRef = React.useRef(false);

  useEffect(() => {
    let cancelled = false;
    profileApi.getTiers()
      .then((res) => { if (!cancelled) setTiers(res.data || []); })
      .catch(() => { if (!cancelled) toast.error('Failed to load membership tiers'); })
      .finally(() => { if (!cancelled) setLoadingTiers(false); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    companyRegistrationApi.getMyRegistration()
      .then((res) => {
        if (!cancelled && res.data?.exists) {
          setCompanyRegistration(res.data);
        }
      })
      .catch((error) => {
        if (!cancelled && error.response?.status !== 404) {
          console.error('Failed to load company registration:', error);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  const currentTierNormalized = normalizeTierForCompare(member?.tier);
  const currentTierRank = getTierRank(member?.tier);
  const isRegisteredUser = currentTierNormalized === 'registered_user';
  const isCompanyApproved = companyRegistration?.status?.toLowerCase() === 'approved';
  const shouldBlockPayment = isRegisteredUser && !isCompanyApproved;

  const handleUpgrade = async (tier) => {
    if (upgradingRef.current) return;
    if (shouldBlockPayment) {
      setShowRegistrationDialog(true);
      return;
    }

    const targetTierRank = getTierRank(tier?.id || tier?.name || tier?.display_name);
    if (currentTierRank > 0 && targetTierRank > 0 && targetTierRank < currentTierRank) {
      toast.error('Lower tier plans are disabled for your current membership.');
      return;
    }

    setSelectedTier(tier);
    const productId = tierToProductId[tier.id] || `membership_${tier.id}`;
    upgradingRef.current = true;
    setCreatingRecord(true);
    try {
      const res = await paymentsApi.createRecord({
        member_id: member?.member_id,
        company_id: member?.company_id,
        product_id: productId
      });

      setPaymentRecord(res.data);
      setShowPaymentDialog(true);
    } catch (err) {
      toast.error(getErrorMessage(err.response?.data?.detail, 'Failed to create payment record'));
    } finally {
      upgradingRef.current = false;
      setCreatingRecord(false);
    }
  };

  const handlePaymentSuccess = (result) => {
    setShowPaymentDialog(false);
    const tierName = selectedTier?.display_name || selectedTier?.name;
    setSelectedTier(null);
    setPaymentRecord(null);
    if (result?.method === 'bank_transfer') {
      toast.success(`Bank transfer details submitted for ${tierName}. Our team will verify the payment shortly.`);
      return;
    }
    toast.success(`Payment link opened for ${tierName}. Complete the payment in Razorpay to finish the upgrade.`);
  };

  return (
    <div className="space-y-6">
      <div className="text-center max-w-3xl mx-auto">
        <h1 className="text-4xl font-bold text-gray-900">Upgrade Your Membership</h1>
        <p className="text-gray-600 mt-3 text-lg">
          Unlock premium features and enhanced visibility with our membership tiers
        </p>
        <div className="mt-4">
          <Badge className="bg-blue-600 text-white text-sm px-4 py-1">
            Current Plan: {member?.tier || 'Registered User'}
          </Badge>
        </div>
      </div>

      {loadingTiers ? (
        <div className="flex justify-center items-center py-16">
          <Loader2 size={32} className="animate-spin text-blue-600" />
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-12">
        {tiers.map((tier) => {
          const annualPrice = Number(tier.price_annual ?? tier.price ?? 0);
          const monthlyPrice = calculateMonthlyFromAnnual(annualPrice);
          const normalizedTierId = normalizeTierForCompare(tier.id);
          const normalizedTierName = normalizeTierForCompare(tier.name);
          const normalizedTierDisplay = normalizeTierForCompare(tier.display_name);
          const isCurrentTier = normalizedTierId === currentTierNormalized
            || normalizedTierName === currentTierNormalized
            || normalizedTierDisplay === currentTierNormalized;
          const planTierRank = getTierRank(tier.id || tier.name || tier.display_name);
          const isLowerTier = currentTierRank > 0 && planTierRank > 0 && planTierRank < currentTierRank;
          // Show all active tiers: Current Plan for user's tier, Upgrade & Pay for all others

          return (
            <Card
              key={tier.id}
              className={`relative overflow-hidden transition-all duration-300 hover:shadow-xl flex flex-col h-full ${
                isCurrentTier
                  ? 'border-2 border-blue-500 shadow-lg'
                  : isLowerTier
                    ? 'border-gray-200 opacity-75'
                    : 'border-gray-200 hover:border-blue-300'
              }`}
            >
              {isCurrentTier && (
                <div className="absolute top-0 right-0 bg-blue-600 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                  CURRENT PLAN
                </div>
              )}
              {tier.id === 'founder' && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-purple-600 to-blue-600 text-white text-center text-xs font-bold py-2">
                  MOST PREMIUM
                </div>
              )}
              {tier.id === 'platinum' && (
                <div className="absolute top-0 left-0 right-0 bg-gradient-to-r from-green-600 to-emerald-600 text-white text-center text-xs font-bold py-2">
                  MOST POPULAR
                </div>
              )}

              <CardHeader className={tier.id === 'founder' || tier.id === 'platinum' ? 'pt-10' : ''}>
                <div className="text-center">
                  {getTierIcon(tier.id)}
                  <CardTitle className="text-2xl">{tier.display_name || tier.name}</CardTitle>
                  <div className="mt-4">
                    <p className="text-3xl md:text-4xl font-bold leading-tight text-gray-900">
                      {formatUsd(monthlyPrice)}
                      <span className="text-2xl md:text-3xl font-semibold">/month</span>
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      billed annually at {formatUsd(annualPrice)}/year
                    </p>
                  </div>
                  <p className="text-sm text-gray-600 mt-3">{tier.description}</p>
                </div>
              </CardHeader>

              <CardContent className="flex flex-col flex-1">
                <ul className="space-y-3 flex-1">
                  {(tier.features || []).map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-start">
                      <Check size={18} className="text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>

                <div className="pt-4 mt-4 border-t space-y-1 text-sm text-gray-600">
                  <p>📝 {formatTierLimit(tier.max_quotes_per_month, 'quotes/month')}</p>
                  <p>📋 {formatTierLimit(tier.max_enquiries_per_month, 'enquiries/month')}</p>
                  <p>👥 {formatTierLimit(tier.max_team_members, 'team members')}</p>
                </div>

                <div className="mt-auto pt-6">
                <Button
                  className={`w-full ${isLowerTier
                    ? 'bg-gray-300 text-gray-600 cursor-not-allowed'
                    : isCurrentTier
                      ? 'bg-gray-300 text-gray-600 cursor-not-allowed border border-gray-300'
                      : 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white'}`}
                  onClick={() => !isLowerTier && !isCurrentTier && handleUpgrade(tier)}
                  disabled={creatingRecord || isLowerTier || isCurrentTier}
                >
                  {creatingRecord ? (
                    <>
                      <Loader2 size={16} className="mr-2 animate-spin" />
                      Preparing...
                    </>
                  ) : shouldBlockPayment ? (
                    <>
                      <CreditCard size={16} className="mr-2" />
                      Complete Registration
                    </>
                  ) : isLowerTier ? (
                    <>
                      <CreditCard size={16} className="mr-2" />
                      Lower Tier Disabled
                    </>
                  ) : isCurrentTier ? (
                    <>
                      <CreditCard size={16} className="mr-2" />
                      Current Plan
                    </>
                  ) : (
                    <>
                      <CreditCard size={16} className="mr-2" />
                      Upgrade & Pay
                    </>
                  )}
                </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
      )}

      <Card className="mt-12 bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200">
        <CardContent className="p-8">
          <div className="text-center max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-3">Need Help Choosing?</h3>
            <p className="text-gray-700 mb-6">
              Our team is here to help you select the right membership tier for your business needs.
            </p>
            <div className="space-y-3">
              <p className="text-sm text-gray-700">
                <a href="mailto:info@oneglobealliance.com" className="text-blue-700 hover:underline">
                  info@oneglobealliance.com
                </a>
                {' '}|{' '}
                <a href="tel:+919892105006" className="text-blue-700 hover:underline">
                  +91 989-210-5006
                </a>
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={(open) => !open && setShowPaymentDialog(false)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upgrade to {selectedTier?.display_name || selectedTier?.name}</DialogTitle>
            <DialogDescription className="sr-only">
              Review payment details and complete the membership upgrade.
            </DialogDescription>
          </DialogHeader>
          {selectedTier && paymentRecord && (
            <PaymentGateway
              paymentId={paymentRecord.payment_id}
              amount={paymentRecord.amount}
              tierAnnualPrice={Number(selectedTier.price_annual ?? selectedTier.price ?? 0)}
              pricingBreakdown={paymentRecord.pricing_breakdown}
              description={`${selectedTier.display_name || selectedTier.name} - Annual Membership`}
              productId={tierToProductId[selectedTier.id] || `membership_${selectedTier.id}`}
              gstDetails={paymentRecord.gst_details}
              onSuccess={handlePaymentSuccess}
              onCancel={() => setShowPaymentDialog(false)}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={showRegistrationDialog} onOpenChange={setShowRegistrationDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Complete Company Registration</DialogTitle>
            <DialogDescription className="sr-only">
              Company registration approval is required before paid membership upgrades are available.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-gray-700">
            <p>
              Registered users remain on the Registered User tier until their company registration is approved.
              Payment is not available yet.
            </p>
            <p>
              Complete your company registration and wait for admin approval before upgrading your membership.
            </p>
            <div className="flex justify-end">
              <Button variant="outline" onClick={() => setShowRegistrationDialog(false)}>
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Upgrade;
